import type { R18OnboardingEvent } from "@/features/r18-blur-onboarding/onboardingMediator";
import { useR18ModalPresenter } from "@/features/r18-blur-onboarding/useOnboardingPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Button, Toggle } from "@/shared/ui/form";
import { Modal } from "@/shared/ui/overlay";

interface R18BlurOnboardingModalProps {
  isOpen: boolean;
  /** backdrop/Escapeで閉じたとき。承認記録はしない(次回また表示する)。 */
  onDismiss: () => void;
  /** OK押下。案内の承認記録と非表示化は呼び出し元の責務。 */
  onComplete: () => void;
}

/**
 * 初来訪者向けのR18ぼかし(クッション)機能の案内モーダル。
 * 未選択の初回来訪者だけ showR18 を false (クッション表示) へ倒し、操作前でも
 * 背後のR-18画像がぼかされた状態にする。保存済みの選択は再表示時も維持する。
 * トグルは設定と直結しており切り替えは即時反映される。OKは承認記録と現在の
 * 選択の確定だけを行い、設定はトグルで選んだままになる。
 */
export const R18BlurOnboardingModal: React.FunctionComponent<
  R18BlurOnboardingModalProps
> = ({ isOpen, onDismiss, onComplete }: R18BlurOnboardingModalProps) => {
  const presentation = useR18ModalPresenter(isOpen, onDismiss, onComplete);
  return (
    <ComponentScope node={presentation.node}>
      <R18BlurOnboardingView
        isOpen={isOpen}
        showR18={presentation.showR18}
        onEvent={presentation.node.dispatch}
      />
    </ComponentScope>
  );
};

interface R18BlurOnboardingViewProps {
  isOpen: boolean;
  showR18: boolean;
  onEvent: (event: R18OnboardingEvent) => void;
}
export const R18BlurOnboardingView: React.FunctionComponent<
  R18BlurOnboardingViewProps
> = ({ isOpen, showR18, onEvent }: R18BlurOnboardingViewProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={(): void => onEvent({ type: "r18-onboarding.close" })}
      ariaLabel="R-18画像の表示方法の確認"
    >
      <fieldset
        className="m-0 min-w-0 space-y-4 border-0 p-4"
        data-nosnippet=""
      >
        {/* 全ページ共通の案内文を検索結果の見出し・スニペット候補にしない。 */}
        <legend className="w-full text-lg font-bold text-foreground">
          R-18画像をクッションなしで表示しますか？
        </legend>
        <p className="flex items-center justify-center gap-3 rounded-lg bg-muted px-4 py-4">
          <b className="text-sm font-bold text-foreground">いいえ</b>
          <Toggle
            checked={showR18}
            onChange={(showR18: boolean): void =>
              onEvent({ type: "r18-onboarding.preference", showR18 })
            }
            aria-label="R-18画像をクッションなしで表示"
          />
          <b className="text-sm font-bold text-foreground">はい</b>
        </p>
        <p className="text-xs text-muted-foreground">
          外出先閲覧用に、R-18画像をクッション表示する機能を用意しています。
          クッションはタップで剥がすことができます。
          クッションのONOFFは、設定欄からいつでも変えられます。
        </p>
        <Button
          variant="primary"
          onClick={(): void => {
            onEvent({ type: "r18-onboarding.confirm" });
          }}
          className="w-full"
        >
          OK
        </Button>
      </fieldset>
    </Modal>
  );
};
